import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { assignSpareToBike, getBranchInventory } from "@/integrations/supabase/inventory-services";
import { useAuth } from "@/context/AuthContext";
import { Bike } from "@/types/models";
import { BranchInventoryWithRelations } from "@/types/inventory";

interface AssignSparePartModalProps {
  isOpen: boolean;
  onClose: () => void;
  bike: Bike | null;
  onSuccess: () => void;
}

const AssignSparePartModal = ({ isOpen, onClose, bike, onSuccess }: AssignSparePartModalProps) => {
  const [sparePartId, setSparePartId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [notes, setNotes] = useState("");
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: inventory, isLoading: isLoadingInventory } = useQuery<BranchInventoryWithRelations[]>({
    queryKey: ['branch-inventory', profile?.branch_id],
    queryFn: () => getBranchInventory(profile?.branch_id || ''),
    enabled: !!profile?.branch_id,
  });

  const selectedInventoryItem = inventory?.find(
    (item) => item.spare_part_type_id === sparePartId
  );

  const assignMutation = useMutation({
    mutationFn: ({ bikeId, sparePartTypeId, branchId, quantity, notes }: {
      bikeId: string;
      sparePartTypeId: string;
      branchId: string;
      quantity: number;
      notes?: string;
    }) => assignSpareToBike(bikeId, sparePartTypeId, branchId, quantity, notes),
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Repuesto asignado a la bicicleta correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['branch-inventory', profile?.branch_id] });
      queryClient.invalidateQueries({ queryKey: ['bike-repair-history', bike?.id] });
      onSuccess();
      handleClose();
    },
    onError: (error: unknown) => {
      console.error("Error assigning spare to bike:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al asignar el repuesto.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sparePartId || !quantity || !bike || !profile?.branch_id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor completa todos los campos requeridos.",
      });
      return;
    }

    const quantityNum = parseInt(quantity);
    if (!selectedInventoryItem || quantityNum <= 0 || quantityNum > selectedInventoryItem.quantity) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "La cantidad debe ser mayor a 0 y menor o igual al stock disponible.",
      });
      return;
    }

    assignMutation.mutate({
      bikeId: bike.id,
      sparePartTypeId: sparePartId,
      branchId: profile.branch_id,
      quantity: quantityNum,
      notes: notes.trim() || undefined
    });
  };

  const handleClose = useCallback(() => {
    setSparePartId("");
    setQuantity("1");
    setNotes("");
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen, handleClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Asignar Repuesto a Bicicleta</DialogTitle>
        </DialogHeader>

        {bike && (
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="font-medium">Bicicleta: #{bike.number} - {bike.model}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="spare-part">Repuesto</Label>
            <Select value={sparePartId} onValueChange={setSparePartId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un repuesto" />
              </SelectTrigger>
              <SelectContent>
                {isLoadingInventory ? (
                  <SelectItem value="loading" disabled>Cargando...</SelectItem>
                ) : (
                  inventory?.map((item) => (
                    <SelectItem key={item.spare_part_type_id} value={item.spare_part_type_id}>
                      {item.spare_part_type?.name} (Stock: {item.quantity})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity">Cantidad Utilizada</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              max={selectedInventoryItem?.quantity || 0}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Cantidad"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas del Trabajo (Opcional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Descripción del trabajo realizado..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={assignMutation.isPending}>
              {assignMutation.isPending ? "Asignando..." : "Asignar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssignSparePartModal;
